import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaqAccordionItem } from '../ds/FaqAccordionItem';
import type { FaqSection } from '../../content/faq';

type FaqAccordionListProps = {
  sections: readonly FaqSection[];
};

type FlatFaq = {
  key: string;
  q: string;
  a: string;
};

export function FaqAccordionList({ sections }: FaqAccordionListProps) {
  const flatItems = useMemo<FlatFaq[]>(
    () =>
      sections.flatMap((section) =>
        section.items.map((item, i) => ({
          key: `${section.id}-${i}`,
          q: item.q,
          a: item.a,
        })),
      ),
    [sections],
  );

  const [openKey, setOpenKey] = useState<string | null>(flatItems[0]?.key ?? null);

  return (
    <div className="flex flex-col gap-10">
      {sections.map((section, sectionIndex) => (
        <div key={section.id}>
          <div className={sectionIndex > 0 ? 'pt-2 border-t border-[var(--color-outline-variant)]' : ''}>
            <p className="label-mono mb-3">{section.title}</p>
            {section.href ? (
              <Link
                to={section.href}
                className="font-display text-[15px] tracking-[-0.01em] text-primary hover:text-[#ffcfbf] transition-colors"
              >
                View solution page →
              </Link>
            ) : null}
          </div>

          <div className="flex flex-col mt-6">
            {section.items.map((item, i) => {
              const key = `${section.id}-${i}`;
              const flatIndex = flatItems.findIndex((f) => f.key === key);
              const isOpen = openKey === key;

              return (
                <FaqAccordionItem
                  key={key}
                  item={item}
                  isOpen={isOpen}
                  showDivider={flatIndex > 0}
                  onToggle={() => setOpenKey(isOpen ? null : key)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
